import { Fragment, type ReactNode } from "react";

/**
 * A small, safe Markdown renderer for the legal documents: headings, paragraphs, bullet and numbered lists, block quotes,
 * tables, bold, italic, code and links. Everything is built as React elements (no HTML injection), links open in a new tab.
 */
function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const tok = m[0];
    const key = `${keyBase}-${i++}`;
    if (tok.startsWith("**")) out.push(<strong key={key}>{inline(tok.slice(2, -2), key)}</strong>);
    else if (tok.startsWith("`")) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok) ?? [];
      if (label && href && /^(https?:|mailto:)/i.test(href))
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>,
        );
      else out.push(label ?? tok);
    } else out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    last = at + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());

export function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const key = `b${k++}`;
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const Tag = `h${Math.min(level + 1, 6)}` as "h2" satisfies string;
      blocks.push(
        <Tag key={key} className={level === 1 ? "t-title-md" : "t-title-sm"}>
          {inline(h[2]!, key)}
        </Tag>,
      );
      i += 1;
    } else if (line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && (lines[i] ?? "").startsWith(">")) {
        quote.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i += 1;
      }
      blocks.push(
        <blockquote key={key} className="gp-quote t-body-sm">
          {inline(quote.join(" "), key)}
        </blockquote>,
      );
    } else if (/^\s*[-*]\s+/.test(line) || /^\s*\d+[.)]\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*]\s+/).test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\s*(?:[-*]|\d+[.)])\s+/, ""));
        i += 1;
      }
      const List = ordered ? "ol" : "ul";
      blocks.push(
        <List key={key} className="t-body">
          {items.map((it, n) => (
            <li key={n}>{inline(it, `${key}-${n}`)}</li>
          ))}
        </List>,
      );
    } else if (line.trim().startsWith("|") && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1] ?? "")) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        rows.push(cells(lines[i] ?? ""));
        i += 1;
      }
      blocks.push(
        <div key={key} className="gp-table-wrap">
          <table className="gp-table">
            <thead>
              <tr>
                {head.map((c, n) => (
                  <th key={n} scope="col">
                    {inline(c, `${key}h${n}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, n) => (
                <tr key={n}>
                  {r.map((c, m) => (
                    <td key={m}>{inline(c, `${key}r${n}c${m}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else {
      const para: string[] = [];
      while (
        i < lines.length &&
        (lines[i] ?? "").trim() &&
        !/^(#{1,4}\s|>|\s*[-*]\s|\s*\d+[.)]\s|\s*\|)/.test(lines[i] ?? "")
      ) {
        para.push((lines[i] ?? "").trim());
        i += 1;
      }
      if (para.length === 0) {
        para.push(line.trim());
        i += 1;
      }
      blocks.push(
        <p key={key} className="t-body">
          {inline(para.join(" "), key)}
        </p>,
      );
    }
  }
  return (
    <div className="gp-prose">
      {blocks.map((b, n) => (
        <Fragment key={n}>{b}</Fragment>
      ))}
    </div>
  );
}
